import React, { useState, useEffect } from 'react';
import { Pencil, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Resource } from '../../lib/resources';
import { getResourceRoles, ResourceRole, assignRoleToResource } from '../../lib/resourceRoles';
import { useBusiness } from '../../contexts/BusinessContext';

interface PermissionRoleSectionProps {
  resource: Resource;
  editMode: string | null;
  onEdit: (section: string) => void;
  onUpdate: () => void;
}

export default function PermissionRoleSection({
  resource,
  editMode,
  onEdit,
  onUpdate
}: PermissionRoleSectionProps) {
  const { selectedBusiness } = useBusiness();
  const [permissionRoles, setPermissionRoles] = useState<ResourceRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>(resource.role_id || '');
  const navigate = useNavigate();

  const loadPermissionRoles = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const roles = await getResourceRoles(selectedBusiness.id);
      setPermissionRoles(roles);
    } catch (err) {
      console.error('Error loading permission roles:', err);
    }
  };

  useEffect(() => {
    if (editMode === 'permissions') {
      loadPermissionRoles();
      setSelectedRoleId(resource.role_id || '');
    }
  }, [editMode, resource.role_id]);

  const handleSave = async () => {
    try {
      setLoading(true);
      await assignRoleToResource(resource.id, selectedRoleId || null);
      onUpdate();
      onEdit(''); // Exit edit mode
    } catch (err) {
      console.error('Error saving permission role:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedRoleId(resource.role_id || '');
    onEdit('');
  };

  const handleConfigureRoles = () => {
    navigate('/teams/permissions');
  };

  return (
    <div>
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <h3 className="text-base font-semibold text-gray-900">Permission Role</h3>
          <button
            onClick={handleConfigureRoles}
            className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors flex items-center"
          >
            <Settings className="h-3 w-3 mr-1" />
            Configure Roles
          </button>
        </div>
        {editMode === 'permissions' ? (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCancel}
              className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100/80 backdrop-blur-xl rounded-xl hover:bg-gray-200/80 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
              style={{ 
                minHeight: '32px',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
              style={{ minHeight: '32px' }}
            >
              {loading ? (
                <>
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        ) : (
          <button
            onClick={() => onEdit('permissions')}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100/50 hover:bg-gray-200/50 text-gray-600 hover:text-gray-700 transition-all duration-200 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="p-2 sm:p-6">
        {editMode === 'permissions' ? (
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
              Current Permission Role
            </label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
              style={{ 
                minHeight: '44px',
                fontSize: '16px', // Prevent iOS zoom
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            >
              <option value="">No role assigned</option>
              {permissionRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <div className="text-xs sm:text-sm text-gray-600 font-medium">Current Permission Role</div>
            <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">
              {resource.permission_role?.name || 'No role assigned'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 